import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import FormField from '../../components/ui/FormField';
import type { SchemaFieldProps } from '../types';

/**
 * Address jsonb shape persisted by the backend (`members.address`).
 */
export interface AddressValue {
  city?: string;
  cityDistrict?: string;
  street?: string;
  zipCode?: string;
  buildingNumber?: string;
  streetNumber?: string;
}

/**
 * Custom SchemaForm widget for the member `address` jsonb field. Wraps the
 * existing AddressAutocomplete (Nominatim search + "fill") plus the flat
 * city/street/zip/buildingNumber/streetNumber inputs. value is the address
 * object (or null); onChange emits the merged object.
 *
 * Extracted from MemberForm's former inline address block.
 */
const AddressField: React.FC<SchemaFieldProps> = ({ field, value, onChange, error }) => {
  const { t } = useTranslation();
  const address: AddressValue = value ?? {};

  // When the org requires an address it requires a DELIVERABLE one: street,
  // house number, city and ZIP (see utils/validation.missingAddressParts).
  // cityDistrict and the second house number stay optional.
  const req = field.required;

  const setPart = useCallback(
    (part: keyof AddressValue, partValue: string) => {
      onChange({ ...address, [part]: partValue });
    },
    [address, onChange]
  );

  const handleAddressSelect = useCallback(
    (selected: {
      street?: string;
      buildingNumber?: string;
      streetNumber?: string;
      city?: string;
      cityDistrict?: string;
      zipCode?: string;
    }) => {
      // Merge only the fields the autocomplete returned, preserving the rest.
      const merged: AddressValue = { ...address };
      if (selected.street !== undefined) merged.street = selected.street;
      if (selected.buildingNumber !== undefined) merged.buildingNumber = selected.buildingNumber;
      if (selected.streetNumber !== undefined) merged.streetNumber = selected.streetNumber;
      if (selected.city !== undefined) merged.city = selected.city;
      if (selected.cityDistrict !== undefined) merged.cityDistrict = selected.cityDistrict;
      if (selected.zipCode !== undefined) merged.zipCode = selected.zipCode;
      onChange(merged);
    },
    [address, onChange]
  );

  return (
    <div className="form-section schema-form__section">
      <h4 className="form-section__title">
        {t(field.labelKey, field.name)}
        {field.required && (
          <span style={{ color: 'var(--danger, #dc3545)', marginLeft: '0.25rem' }}>*</span>
        )}
      </h4>
      {error && (
        <span
          className="form-error"
          style={{ color: 'var(--danger, #dc3545)', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}
        >
          {error}
        </span>
      )}
      <AddressAutocomplete onAddressSelect={handleAddressSelect} />
      <div className="form-grid">
        <FormField
          htmlFor="address-street"
          label={t('members.street')}
          required={req}
          className="form-grid--full"
          >
          <input
            type="text"
            id="address-street"
            placeholder={t('members.enterStreet')}
            value={address.street ?? ''}
            onChange={(e) => setPart('street', e.target.value)}
          />
        </FormField>
        <FormField
          htmlFor="address-buildingNumber"
          label={t('members.buildingNumber')}
          required={req}
          >
          <input
            type="text"
            id="address-buildingNumber"
            placeholder={t('members.enterBuildingNumber')}
            value={address.buildingNumber ?? ''}
            onChange={(e) => setPart('buildingNumber', e.target.value)}
          />
        </FormField>
        <FormField
          htmlFor="address-streetNumber"
          label={t('members.streetNumber')}
          >
          <input
            type="text"
            id="address-streetNumber"
            placeholder={t('members.enterStreetNumber')}
            value={address.streetNumber ?? ''}
            onChange={(e) => setPart('streetNumber', e.target.value)}
          />
        </FormField>
        <FormField
          htmlFor="address-city"
          label={t('members.city')}
          required={req}
          >
          <input
            type="text"
            id="address-city"
            placeholder={t('members.enterCity')}
            value={address.city ?? ''}
            onChange={(e) => setPart('city', e.target.value)}
          />
        </FormField>
        <FormField
          htmlFor="address-cityDistrict"
          label={t('members.cityDistrict')}
          >
          <input
            type="text"
            id="address-cityDistrict"
            placeholder={t('members.enterCityDistrict')}
            value={address.cityDistrict ?? ''}
            onChange={(e) => setPart('cityDistrict', e.target.value)}
          />
        </FormField>
        <FormField
          htmlFor="address-zipCode"
          label={t('members.zipCode')}
          required={req}
          >
          <input
            type="text"
            id="address-zipCode"
            placeholder={t('members.enterZipCode')}
            value={address.zipCode ?? ''}
            onChange={(e) => setPart('zipCode', e.target.value)}
          />
        </FormField>
      </div>
    </div>
  );
};

export default AddressField;
